import {SE_SYNC_TYPES} from "@/models/SE_SYNC_TYPES";
import store from '@/store/store'
import deepIsEqual from "@/utils/deepIsEqual";
import {computed} from "vue";

export default function useTranslations(){

  function composeSectionTranslationsStatuses(sections) {
    const result = {};
    for (const sectionName of Object.keys(sections)) {
      const lang = sections[sectionName].lang;
      result[sectionName] = { lang: {} };
      const section = result[sectionName].lang;
      for (const langISO of Object.keys(lang)) {
        if (Object.hasOwn(lang, langISO)) {
          section[langISO].translated = isLangPopulated(lang[langISO]);
        }
      }
    }
    return result;
  }

  function isLangPopulated(langObj){
    let isTranslated = true;
    for (const langContent of Object.values(langObj)) {
      // if langContent is non-empty array
      if(Array.isArray(langContent) && langContent.length !== 0){
        for (const langContentValue of Object.values(langContent)) {
          if(typeof langContentValue === 'object'){
            for (const val of Object.values(langContentValue)) {
              if(!val || (Array.isArray(val) && val.length === 0)){
                isTranslated = false;
                break;
              }
            }
          }
        }
      }
      // if langContent is an empty array
      /*if(Array.isArray(langContent) && langContent.length === 0){
        isTranslated = false;
        break;
      }*/
      // if langContent isn't array && has no value
      if(!Array.isArray(langContent) && !langContent){
        isTranslated = false;
        break;
      }
    }
    return isTranslated;
  }

  // checks if translated status has been changed
  function getTranslatedDiff(initial, current){
    let diff = null;
    for(const lang of Object.keys(initial)){
      if(initial[lang].translated !== current[lang].translated){
        if(!diff) diff = {};
        diff[lang] = {translated: current[lang].translated};
      }
    }
    return diff;
  }

  function getTranslatedTotal(array) {
    // collect lang ISO from the array of objects
    const result = {};
    for (const obj of array) {
      for (const [iso, value] of Object.entries(obj.lang)) {
        // add iso to result if it doesn't exist
        if(!Object.hasOwn(result, iso)){
          result[iso] = {translated: true};
        }
        // if !translated set result translated to false
        if(!value.translated){
          result[iso].translated = false;
        }
      }
    }
    return result;
  }

  // syncs translated status
  function syncTranslatedEdits({data, section}){
    /**
     * data: Object
     * {
     *   en: {translated: true},
     *   de: {translated: false},
     *   ...
     * }
     * */
    const editsData = {
      type: SE_SYNC_TYPES.TRANSLATED_STATUS,
      data: {
        section, //: POPUP_SETTINGS.WEB.SECTIONS["0"].SEO_SETTINGS.VALUE,
        data
      }
    }
    store.dispatch("simultaneousEditing/syncEdits", editsData);
  }

  // CHECK DEFAULT LANGUAGE FOR CHANGES TO UNCHECK TRANSLATED STATUS OF ADDITIONAL LANGUAGES
  const language = computed(() => store.getters["maps/languages/getLanguageComputed"]);

  function mainLanguageCheckObjectChanges({lang, originalLang}){
    /**
     * lang, originalLang: Object, contains ISO
     * {
     *   en: {...},
     *   de: {...},
     *   ...
     * }
     * */
    // language and languagesChosen are part of any section so no need to provide it via params
    const isChanged = !deepIsEqual(lang[language.value.id], originalLang[language.value.id]);
    if(!isChanged) return false;
    // if changed, get those languages that have been translated before the current session
    for(const [key, object] of Object.entries(originalLang)){
      if((key !== language.value.id) && object.translated) {
        // ensure additional language has not changed this session
        const haveChanges = !deepIsEqual(lang[key], object);
        if(!haveChanges){
          // change provided object
          lang[key].translated = false;
        }
      }
    }
    return true;
  }

  function mainLanguageCheckArrayChanges({edited, original}){
    /**
     * edited, original: Array, contains the lang object and other properties
     * [
     *   ...
     *   id: Number,
     *   lang: {}
     * ]
     * */
    let isChanged = false;
    edited.forEach((item, $itemIndex) => {
      // no need to check newly created item
      if(!item.created){
        const originalLang = original.find(originalItem => item.id === originalItem.id)?.lang;
        if(!originalLang) return;
        const result = mainLanguageCheckObjectChanges({lang: item.lang, originalLang});
        if(result) isChanged = true;
      }
    })
    return isChanged;
  }

  function composeIntoLang({value, field, currentLang, langObj, replace = false}){
    const result = {lang: {[currentLang]: {[field]: value}}};
    for(const [lang, object] of Object.entries(langObj)){
      if(!object[field] && !object.translated/* && lang !== currentLang*/){
        Object.assign(result.lang, {[lang]: {[field]: replace ? replaceLang(value, lang) : value}})
      }
    }
    return result
  }

  function getSubvariants({id, lang}){
    const result = [];
    for(const iso of Object.keys(lang)){
      if(iso.includes(id)) result.push(iso);
    }
    return result;
  }

  function translatedFieldChanged(value, {id, field, langObj, replace = false, array = '', index}){
    if(typeof value === 'object' &&  value !== null && Object.keys(value).includes('focusedEditor')){
      // the presence of focusedEditor implies that it was called by tinyMCE editor
      value = value.target.getContent();
    }
    if(value){
      // if updated field changed on default language tab,
      // update all non-empty non-translated additional languages
      if(id === language.value.id){
        for(const lang of Object.keys(langObj)){
          const target = array ? langObj[lang][array][index] : langObj[lang];
          if(lang !== id && !target[field] && !target.translated){
            target[field] = replace ? replaceLang(value, lang) : value;
          }
        }
      }
      // if the current lang tab isn't a default language,
      // check if there are subvariants of the current language and update them accordingly
      const subvariants = getSubvariants({id, lang: langObj});
      if(subvariants.length && id !== language.value.id){
        subvariants.forEach(lang => {
          const target = array ? langObj[lang][array][index] : langObj[lang];
          if(!target[field] && !target.translated){
            target[field] = replace ? replaceLang(value, lang) : value;
          }
        })
      }
    }
  }

  function replaceLang(content, lang) {
    // Define a regular expression pattern to match the "lang" parameter
    const langPattern = /(\blang=)([a-zA-Z0-9]{2,5})(?=[&#\s?"']|$)/g;

    // Use the replace method with a callback function
    if(!content) return content;
    const replacedContent = content.replace(langPattern, (match, langQuery, oldLang) => {
      // Replace the oldLang with the new "lang" parameter
      const newLang = langQuery + lang;
      return newLang;
    });

    return replacedContent;
  }

  return {
    composeSectionTranslationsStatuses,
    isLangPopulated,
    getTranslatedDiff,
    getTranslatedTotal,
    syncTranslatedEdits,
    mainLanguageCheckObjectChanges,
    mainLanguageCheckArrayChanges,
    composeIntoLang,
    getSubvariants,
    replaceLang,
    translatedFieldChanged,
  }
}